import { computed, EffectRef, inject, Injectable, Signal } from '@angular/core';
import { IHttpResponse, IOtpData, IOtpModel } from '@partners/api';
import { IRemoteError } from '@partners/errors';
import { HttpService, tapResult, tapStatus } from '@partners/http';
import { SettingsMap, TFeature } from '@partners/settings';
import { AuthLoginStore, AuthOtpStore } from '@partners/state';
import { takeOnce } from '@partners/utils';
import { catchError, EMPTY, Observable, tap } from 'rxjs';
import { DialogAdapter } from '../../dialogs';
import { NavigationAdapter } from '../../navigation';
import { AuthOtpSeverities } from './errors';

@Injectable({
  providedIn: 'root'
})
export class AuthFacade {
  isAuthenticated$$: Signal<boolean>;
  isNotAuthenticated$$: Signal<boolean>;

  otpToken$$: Signal<string>;
  isVoiceOtp$$: Signal<boolean>;
  otpPhoneNumber$$: Signal<string>;
  isMultipleOtpSendAttempts$$: Signal<boolean>;

  private _httpService = inject(HttpService);
  private _authOtpStore = inject(AuthOtpStore);
  private _navAdapter = inject(NavigationAdapter);
  private _loginStore = inject(AuthLoginStore);
  private _dialogAdapter = inject(DialogAdapter);

  private _otpSeverityHandler = inject(AuthOtpSeverities);
  private _effectRef: EffectRef;

  constructor() {
    this.isAuthenticated$$ = computed(() => this._loginStore.isLoggedIn$$());
    this.isNotAuthenticated$$ = computed(() => !this.isAuthenticated$$());

    this.otpToken$$ = computed(() => this._authOtpStore.data$$()?.token);
    this.isVoiceOtp$$ = computed(() => this._authOtpStore.model$$()?.isVoiceOtp);
    this.otpPhoneNumber$$ = computed(() => this._authOtpStore.data$$()?.phone);
    this.isMultipleOtpSendAttempts$$ = this._authOtpStore.isMultipleOtpSendAttempts$$;
  }

  ngOnDestroy(): void {
    this._effectRef && this._effectRef.destroy();
  }

  reSendOtp(model?: IOtpModel): Observable<IHttpResponse<IOtpData>> {
    //send isRetryOrDirect indication to stay on page
    return this.sendOtp({ ...this._authOtpStore.model$$(), ...model }, { isRetryOrDirect: true }).pipe(
      catchError((error: IRemoteError) => {
        this.showReSendOtpErrorDialog(error);
        return EMPTY;
      }),
      tapResult<IOtpData>((data: IOtpData) => {
        this._authOtpStore.dispatchUpdateOtpAttemptsCount();
      })
    );
  }

  sendFirstOtp(model: IOtpModel): Observable<IHttpResponse<IOtpData>> {
    return this.sendOtp(model).pipe(
      catchError((error: IRemoteError) => {
        return this._otpSeverityHandler.inspect(error);
      }),
      tapResult<IOtpData>((data: IOtpData) => {
        this._authOtpStore.dispatchUpdateOtpAttemptsCount();
        this._navAdapter.moveNext();
      })
    );
  }

  /**
   * @description
   * sends an otp password to the server for verification
   * @param model and object with the otp value
   * @param options isRetryOrDirect - indication weather this is a recurring attempt to login - or -
   * a direct attempt to login (sms send directly from server to user on load)
   * @returns an observable with the response data
   */
  sendOtp(model: IOtpModel, options?: { isRetryOrDirect?: boolean }): Observable<IHttpResponse<IOtpData>> {
    return this._httpService.post<IOtpModel, IOtpData>('auth-otp', model).pipe(
      tapResult<IOtpData>((data: IOtpData) => {
        this._authOtpStore.dispatchOtpLoadSuccess({ model, data });
      }),
      tapStatus(({ screenName }) => {
        if (screenName) {
          this._navAdapter.moveTo(screenName as TFeature);
          return;
        }
        if (!options?.isRetryOrDirect) {
          this._navAdapter.moveNext();
        }
      })
    );
  }

  private showReSendOtpErrorDialog({ title, message }: IRemoteError) {
    const dialogRef = this._dialogAdapter.openAlert({ title, message });

    dialogRef.onDismiss$
      .pipe(
        takeOnce(),
        tap(() => {
          const { name } = SettingsMap.identity();
          this._navAdapter.moveTo(name);
        })
      )
      .subscribe();
  }
}
